import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IOnlineTourPriceResult } from '../utils/types';
import { ChildInfo } from '../utils';
import { TourPurchaseGuestsInfo } from '../pages/TourPurchase';

export function PurchaseSummary({
  guests,
  guestsInfo,
  currencyCode,
  adultPrice,
  childrenPrices,
  totalPrice,
  room,
  sx,
}: {
  guests: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
  };
  guestsInfo: TourPurchaseGuestsInfo;
  adultPrice: number;
  childrenPrice: number;
  childrenPrices: IOnlineTourPriceResult[];
  totalPrice: number;
  currencyCode: string;
  room: string;
  sx?: SxProps;
}) {
  const { t } = useTranslation();

  return (
    <Card sx={sx}>
      <CardContent>
        <Typography variant="h6">{t('purchaseSummary.title')}</Typography>
        <Typography gutterBottom sx={{ color: 'text.secondary' }}>
          {room}
        </Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <div>
            <Typography>{t('purchasSummary.adult')}</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {guestsInfo.adults
                .map((g) => `${g.firstName} ${g.lastName.slice(0, 1)}.`)
                .join(' - ')}
            </Typography>
          </div>
          <Typography>
            {guests.adults} x{' '}
            {adultPrice.toLocaleString('tr-TR', {
              style: 'currency',
              currency: currencyCode,
              maximumFractionDigits: 0,
            })}
          </Typography>
        </Stack>
        {childrenPrices.map((cp, i) => (
          <Stack
            key={`${cp.id}-${guestsInfo.children[i]?.id || i}`}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <div>
              <Typography>
                {t('purchasSummary.child', {
                  ageGroup: cp.ageGroup,
                  bedStatus: cp.bedStatus,
                })}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {guestsInfo.children
                  .slice(i, i + 1)
                  .map((g) => `${g.firstName} ${g.lastName.slice(0, 1)}.`)
                  .join(' - ')}
              </Typography>
            </div>
            <Typography>
              1 x{' '}
              {cp.price.toLocaleString('tr-TR', {
                style: 'currency',
                currency: currencyCode,
                maximumFractionDigits: 0,
              })}
            </Typography>
          </Stack>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography>{t('purchaseSummary.totalPrice')}</Typography>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '1.2rem',
              color: 'primary.main',
            }}
          >
            {totalPrice.toLocaleString('tr-TR', {
              style: 'currency',
              currency: currencyCode,
              maximumFractionDigits: 0,
            })}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PurchaseSummarySkeleton() {
  const { t } = useTranslation();

  return (
    <Card sx={{ flex: 1 }}>
      <CardContent>
        <Typography variant="h6">{t('purchaseSummary.title')}</Typography>
        <Skeleton variant="text" width={200} sx={{ mb: 0.5 }} />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
        >
          <Box>
            <Typography>{t('purchasSummary.adult')}</Typography>
            <Skeleton variant="text" width={100} />
          </Box>
          <Skeleton variant="text" width={100} />
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('purchaseSummary.totalPrice')}</Typography>
          <Skeleton variant="text" width={100} />
        </Stack>
      </CardContent>
    </Card>
  );
}
